import { memo, useMemo, useCallback } from 'react';
import { useSelector, State } from '@bonanza-social/toolkit/state';
import { useNavigation, StackNavigationProp } from '@bonanza-social/navigation';
import {
  Box,
  Text,
  Image,
  Button,
  Breakpoint,
  useBreakpoint,
  useBreakpointProp,
  useLayout,
} from '@bonanza-social/moonlight';
import { selectCategory } from '../../../../state/categories/categories.slice';
import { getReducedGameIds } from '../Category.utils';
import { CategoryProps } from '../Category.types';
import CustomCategoryFade from './CustomCategoryFade/CustomCategoryFade';
import Games from '../../../Games/Games';

function CustomCategory({ categoryId, ...rest }: CategoryProps) {
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const category = useSelector((state: State) =>
    selectCategory(state, categoryId),
  );
  const reducedGameIds = useMemo(
    () => getReducedGameIds(category?.games),
    [category?.games],
  );
  const {
    description,
    categoryLogo,
    backgroundImage,
    mobileCategoryLogo,
    mobileBackgroundImage,
  } = category?.theme.themeConfiguration || {};
  const { logo, background } = useBreakpointProp({
    mobile: {
      logo: mobileCategoryLogo,
      background: mobileBackgroundImage,
    },
    tablet: {
      logo: categoryLogo,
      background: backgroundImage,
    },
    desktop: {
      logo: categoryLogo,
      background: backgroundImage,
    },
  });
  const breakpoint = useBreakpoint();
  const [backgroundLayout, onBackgroundLayout] = useLayout();
  const [contentLayout, onContentLayout] = useLayout();

  const onPress = useCallback(() => {
    navigation.navigate('CategoryDetailsScreen', { id: categoryId });
  }, [categoryId, navigation]);

  const renderButton = useCallback(
    () => (
      <Button
        locNS="games"
        locKey="showMore"
        alignSelf={{
          mobile: 'center',
          tablet: 'center',
          desktop: 'flex-start',
        }}
        marginTop={
          breakpoint === Breakpoint.DESKTOP ? 'space-500' : 'space-600'
        }
        marginBottom={
          breakpoint === Breakpoint.DESKTOP ? 'space-0' : 'space-400'
        }
        size={breakpoint === Breakpoint.MOBILE ? 'small' : 'large'}
        onPress={onPress}
      />
    ),
    [breakpoint, onPress],
  );

  const renderHeading = useCallback(
    () => (
      <Box
        alignItems={{
          mobile: 'center',
          tablet: 'center',
          desktop: 'flex-start',
        }}
        paddingTop={{
          mobile: 'space-800',
          tablet: 'space-1000',
          desktop: undefined,
        }}
        paddingLeft={{
          mobile: 'space-1000',
          tablet: 'space-3000',
          desktop: 'space-1000',
        }}
        paddingRight={{
          mobile: 'space-1000',
          tablet: 'space-3000',
          desktop: 'space-400',
        }}
        paddingBottom={{
          mobile: 'space-600',
          tablet: 'space-600',
          desktop: undefined,
        }}
        maxWidth={{
          mobile: undefined,
          tablet: undefined,
          desktop: 400,
        }}
      >
        {logo ? (
          <Image
            source={logo}
            contentPosition={
              breakpoint === Breakpoint.DESKTOP ? 'left center' : undefined
            }
            width={{ mobile: 200, tablet: 230, desktop: 255 }}
            height={{ mobile: 80, tablet: 92, desktop: 102 }}
          />
        ) : undefined}
        {description ? (
          <Text
            variant="body-s"
            color="darkSurfacePrimary"
            paddingTop={{
              mobile: 'space-400',
              tablet: 'space-400',
              desktop: 'space-500',
            }}
            textAlign={{
              mobile: 'center',
              tablet: 'center',
              desktop: 'left',
            }}
          >
            {description}
          </Text>
        ) : undefined}
        {breakpoint === Breakpoint.DESKTOP && renderButton()}
      </Box>
    ),
    [logo, description, breakpoint, renderButton],
  );

  if (!category) {
    return;
  }

  return (
    <Box
      height={
        Math.max(backgroundLayout.height, contentLayout.height) || undefined
      }
      {...rest}
    >
      <Box
        position="absolute"
        width={{ mobile: '100%', tablet: '100%', desktop: 608 }}
        aspectRatio={16 / 9}
        onLayout={onBackgroundLayout}
      >
        <Image
          source={background}
          contentFit="cover"
          position="absolute"
          inset={0}
        />
        <CustomCategoryFade direction="top" />
        {breakpoint === Breakpoint.DESKTOP && (
          <CustomCategoryFade direction="right" />
        )}
        <CustomCategoryFade direction="bottom" />
      </Box>
      <Box
        alignItems={{ mobile: undefined, tablet: undefined, desktop: 'center' }}
        flexDirection={{ mobile: 'column', tablet: 'column', desktop: 'row' }}
        marginVertical="auto"
        onLayout={onContentLayout}
      >
        {renderHeading()}
        <Box flex={{ mobile: undefined, tablet: undefined, desktop: 1 }}>
          <Games
            ids={reducedGameIds}
            imageType={category.imageType.name}
            paddingLeft={{
              mobile: 'space-400',
              tablet: 'space-1000',
              desktop: 'space-1600',
            }}
          />
        </Box>
        {breakpoint !== Breakpoint.DESKTOP && renderButton()}
      </Box>
    </Box>
  );
}

export default memo(CustomCategory);
